/**
 * WVB-02 — Financial Overview Tab
 * Displays all 8 KPIs + budget summary + action buttons.
 */
import { useState } from "react";
import { Plus, Send, RefreshCw, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { budgetApi } from "@/services/api";
import FinKpiCard from "./FinKpiCard";
import { BUDGET_STATUS_CONFIG } from "./EventFinancialsUtils";

function StatusBadge({ status }) {
  const cfg = BUDGET_STATUS_CONFIG[status] || { cls: "bg-slate-100 text-slate-500", label: status };
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>{cfg.label}</span>;
}

export default function EventFinancialsOverviewTab({ eid, financials, onRefresh }) {
  const [loading, setLoading] = useState(false);

  const canEdit = financials?.budget_status === "DRAFT";
  const canSubmit = financials?.budget_status === "DRAFT";
  const canRevise = financials?.budget_status === "SUBMITTED";
  const bid = financials?.budget_id;

  async function handleCreateBudget() {
    setLoading(true);
    try {
      await budgetApi.createBudget(eid, { currency_code: "INR" });
      toast.success("Budget created");
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create budget");
    } finally { setLoading(false); }
  }

  async function handleSubmit() {
    if (!bid) return;
    setLoading(true);
    try {
      await budgetApi.submitBudget(eid, bid);
      toast.success("Budget submitted for approval");
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit budget");
    } finally { setLoading(false); }
  }

  async function handleRevise() {
    if (!bid) return;
    setLoading(true);
    try {
      await budgetApi.reviseBudget(eid, bid);
      toast.success("Budget reopened for editing");
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revise budget");
    } finally { setLoading(false); }
  }

  if (!financials) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-400" /></div>;
  }

  const primary8 = [
    financials.contracted_revenue,
    financials.scheduled_collections,
    financials.internal_cost_budget,
    financials.approved_cost_baseline,
    financials.open_commitments,
    financials.actual_costs,
    financials.estimate_to_complete,
    financials.forecast_gross_margin,
  ];
  const derived = [
    financials.total_direct_cost,
    financials.total_contingency,
    financials.total_forecast_cost,
    financials.forecast_gross_profit,
    financials.budget_variance,
  ];

  return (
    <div className="space-y-6" data-testid="financials-overview-tab">
      {/* Budget header + actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {financials.has_budget ? (
            <>
              <span className="text-sm font-semibold text-slate-700">{financials.budget_number}</span>
              <span className="text-xs text-slate-400">v{financials.budget_version}</span>
              <StatusBadge status={financials.budget_status} />
            </>
          ) : (
            <span className="text-sm text-slate-500">No budget created yet for this event.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          {!financials.has_budget && (
            <Button size="sm" onClick={handleCreateBudget} disabled={loading} data-testid="create-budget-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Create Budget
            </Button>
          )}
          {canSubmit && (
            <Button size="sm" onClick={handleSubmit} disabled={loading} data-testid="submit-budget-btn">
              <Send className="w-4 h-4 mr-1.5" /> Submit for Approval
            </Button>
          )}
          {canRevise && (
            <Button size="sm" variant="outline" onClick={handleRevise} disabled={loading} data-testid="revise-budget-btn">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Revise
            </Button>
          )}
        </div>
      </div>

      {/* 8 Primary KPIs */}
      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Core Financial KPIs</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {primary8.map(kpi => kpi && <FinKpiCard key={kpi.key} kpi={kpi} />)}
        </div>
      </div>

      {/* Derived totals */}
      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Budget Breakdown</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {derived.map(kpi => kpi && <FinKpiCard key={kpi.key} kpi={kpi} />)}
        </div>
      </div>

      {/* B3/Wave E notes */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-500 space-y-1">
        <div className="font-semibold text-slate-600 mb-1">Platform Roadmap Notes</div>
        <div>{financials.meta?.b3_note}</div>
        <div>{financials.meta?.wave_e_note}</div>
      </div>
    </div>
  );
}
